import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";

import { Store } from "@ngrx/store";
import { Observable, throwError } from "rxjs";

import { AppState } from "src/app/store/reducers";
import { PaginationRequest } from "src/app/models/common/paginationRequest";
import { AppSessionService } from "../app-session/app-session.service";
import { FilterOperation } from "src/app/models/custom-table/custom-table.model";
import {
  setAdvancedFilterQuery,
  setSimpleFilterQuery,
} from "../../utils/tableUtils";
import { catchError, map } from "rxjs/operators";

const EMPTY_STRING = "";
const NULL_VALUE = null;

@Injectable({
  providedIn: "root",
})
export class SitesService {
  private BASE_URL = this.appSession.baseUrl + "/api/v1/site";

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private appSession: AppSessionService
  ) {}

  public getSites(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    return this.http.get(
      this.buildDataUrl(paging, filteringArgs, sortingArgs, headers, "?"),
      { headers }
    );
  }

  searchOpenStreetMap(street?: string, postalCode?: string, city?: string): Observable<any> {
    const treatedStreet = street?.replace(/ /g,"+");
    const treatedPostalCode = postalCode?.replace(/ /g,"+");
    const treatedCity = city?.replace(/ /g,"+");

    const url = `https://nominatim.openstreetmap.org/search?q=${treatedStreet ?? ''},${treatedPostalCode ?? ''},${treatedCity ?? ''}&format=json&polygon=1&addressdetails=1`;
    return this.http.get(url);
  }

  reverseOpenStreetMap(latitude?: string, longitude?: string): Observable<any> {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`;
    return this.http.get(url);
  }

  getSitesRegions(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/regions"}`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 999,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getSitesProfile(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/sites/profiles"}`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 999,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getWarehouseTypes(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/devices/warehouseTypes"}`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 100,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getPumpTypes(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/devices/pumpTypes"}`;

    if (!pageReq) {
      pageReq = {
        offset: 0,
        limit: 100,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getPriceSignTypes(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/devices/priceSignTypes"}`;

    if (!pageReq) {
      pageReq = {
        offset: 0,
        limit: 100,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getPosTypes(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/devices/posTypes"}`;

    if (!pageReq) {
      pageReq = {
        offset: 0,
        limit: 100,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getDevicesSubTypes(
    pageReq: PaginationRequest,
    code: string
  ): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${
      this.appSession.baseUrl + "/api/v1/devices/deviceSubtypes"
    }?siteDeviceTypeCode=${code}`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 999,
        realSize: false,
      };
    }
    return this.http.get(url, { headers });
  }

  getEnabledSites(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    return this.http.get(
      this.buildDataUrl(paging, filteringArgs, sortingArgs, headers, "?"),
      { headers }
    );
  }

  createSite(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.site);
    const url = `${this.appSession.baseUrl + "/api/v1/site"}`;
    return this.http.post(url, data, { headers });
  }

  updateSite(site): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(site);
    const url = `${this.appSession.baseUrl + "/api/v1/site"}/${site.code}`;
    return this.http.put(url, data, { headers });
  }

  getSite(code: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.appSession.baseUrl + "/api/v1/site"}/${code}`;
    return this.http.get(url, { headers });
  }

  getSiteWarehouses(code: string, pageReq?: PaginationRequest): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let url = `${this.appSession.baseUrl}/api/v1/sites/${code}/devices/warehouses`;

    if (pageReq) {
      url = this.addUrlPagination(url, pageReq);
    }

    return this.http.get(url, { headers });
  }

  getSiteFuelpoints(code: string, pageReq?: PaginationRequest): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let url = `${this.appSession.baseUrl}/api/v1/sites/${code}/devices/fuelPoints`;

    if (pageReq) {
      url = this.addUrlPagination(url, pageReq);
    }

    return this.http.get(url, { headers });
  }

  getSitePriceSigns(code: string, pageReq?: PaginationRequest): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let url = `${this.appSession.baseUrl}/api/v1/sites/${code}/devices/priceSigns`;

    if (pageReq) {
      url = this.addUrlPagination(url, pageReq);
    }

    return this.http.get(url, { headers });
  }

  getSitePos(code: string, pageReq?: PaginationRequest): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let url = `${this.appSession.baseUrl}/api/v1/sites/${code}/devices/pos`;

    if (pageReq) {
      url = this.addUrlPagination(url, pageReq);
    }

    return this.http.get(url, { headers });
  }

  getSiteDispensers(code: string, pageReq?: PaginationRequest): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let url = `${this.appSession.baseUrl}/api/v1/sites/${code}/devices/fuelDispensers`;

    if (pageReq) {
      url = this.addUrlPagination(url, pageReq);
    }

    return this.http.get(url, { headers });
  }

  getSiteFccs(code: string, pageReq?: PaginationRequest): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let url = `${this.appSession.baseUrl}/api/v1/sites/${code}/devices/fcc`;

    if (pageReq) {
      url = this.addUrlPagination(url, pageReq);
    }

    return this.http.get(url, { headers });
  }

  getSiteProductList(code): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${
      this.appSession.baseUrl + `/api/v1/siteProducts/search/${code}`
    }`;

    return this.http.get(url, { headers });
  }

  getSiteFuelProductList(code): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${
      this.appSession.baseUrl + `/api/v1/siteProducts/search/${code}?isFuel=true`
    }`;

    return this.http.get<any>(url, { headers }).pipe(
      map(( {result} ) => result),
      catchError((error) => throwError(error))
    );
  }

  getSiteStoreProductList(code): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${
      this.appSession.baseUrl + `/api/v1/siteProducts/search/${code}?isFuel=false`
    }`;

    return this.http.get(url, { headers });
  }

  // With pagination, query and sorting
  getSiteStoreProductListPagination(
    code: string,
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any,
    isFuel?: boolean
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = "";
    let advancedFilterQuery = "";
    let orderQuery = "";

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }
    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }
    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    // Build URL manually but follow getSites() structure for paging
    const baseUrl = `${this.appSession.baseUrl}/api/v1/siteProducts/search/${code}?isFuel=${isFuel}`;
    const pagingExpr = paging ? this._buildScrollExpression(paging) : "";
    const urlWithPaging = pagingExpr ? `${baseUrl}&${pagingExpr}` : baseUrl;

    return this.http.get(urlWithPaging, { headers });
  }

  //Link/Unlik New Products List For Store Products
  updateSiteStoreProductList(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const { siteCode, productsToLinkToSite } = action;
    const data = JSON.stringify({
      siteCode,
      productsToLinkToSite,
    });
    const url = `${this.appSession.baseUrl}/api/v1/siteProducts/link`;

    return this.http.post(url, data, { headers });
  }

  createSiteStoreProductList(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const { siteCode, productList } = action;
    const data = JSON.stringify(productList);
    const url = `${this.appSession.baseUrl}/api/v1/siteProducts/${siteCode}`;
    return this.http.post(url, data, { headers });
  }

  getSiteGradeList(code): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${
      this.appSession.baseUrl + `/api/v1/siteGrades/${code}`
    }`;

    return this.http.get(url, { headers });
  }

  createSiteGradeList(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const { siteCode, gradeList } = action;
    const data = JSON.stringify(gradeList);
    const url = `${this.appSession.baseUrl}/api/v1/siteGrades/${siteCode}`;

    return this.http.post(url, data, { headers });
  }

  getSitePeripheralList(code: string, pageReq?: PaginationRequest): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let url = `${this.appSession.baseUrl}/api/v1/sites/${code}/devices/peripherals`;

    if (pageReq) {
      url = this.addUrlPagination(url, pageReq);
    }

    return this.http.get(url, { headers });
  }

  getSiteMultimediaList(code: string, pageReq?: PaginationRequest): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let url = `${this.appSession.baseUrl}/api/v1/sites/${code}/devices/multimedia`;

    if (pageReq) {
      url = this.addUrlPagination(url, pageReq);
    }

    return this.http.get(url, { headers });
  }

  // Grid filter helpers block

  private buildDataUrl(
    pagingArgs: any,
    filteringArgs: any,
    sortingArgs: any,
    header: HttpHeaders,
    path: string
  ): string {
    let baseQueryString = `${this.appSession.baseUrl + "/api/v1/site"}${path}`;
    let scrollingQuery = EMPTY_STRING;
    let query = EMPTY_STRING;

    if (pagingArgs) {
      scrollingQuery = this._buildScrollExpression(pagingArgs);
    }
    query +=
      scrollingQuery !== EMPTY_STRING ? `&${scrollingQuery}` : EMPTY_STRING;

    baseQueryString += query;

    return baseQueryString;
  }

  private _buildScrollExpression(pagingArgs): string {
    const skip = pagingArgs.skip;
    const top = pagingArgs.top;
    const realSize = pagingArgs.realSize;

    if (top && top !== null) {
      return `real_size=${!!realSize}&offset=${skip}&limit=${top}`;
    } else {
      return `real_size=${!!realSize}&offset=${skip}`;
    }
  }

  private addUrlPagination(url: string, pageReq: PaginationRequest): string {
    const { realSize, offset, limit } = pageReq;
    return `${url}?real_size=${realSize}&offset=${offset}&limit=${limit}`;
  }
}
